import { TESLA_FACTS, type TeslaModelKey } from './tesla-facts'
import { TESLA_MODELS, modelSlugFromCompatibleModelString } from './compatibility'

/**
 * Logic behind the /guide "Check my car" picker.
 *
 * Every sentence a visitor reads comes from TESLA_FACTS. The model list and the
 * first model year of each line come from the `tesla_models` table; the
 * speaker cutoffs come from TESLA_FACTS.support.speakerSince. The table's own
 * `supports_custom_lock_sound`, `software_version_min` and
 * `installation_notes` columns are not read: they say every year of every model
 * works, quote software 2020.48, and tell the owner to use the USB root, all of
 * which disagree with the facts file.
 */

export interface CarModel {
  key: TeslaModelKey
  name: string
  firstYear: number
}

export type CarStatus = 'yes' | 'check' | 'unlikely'

export interface CarVerdict {
  status: CarStatus
  /** One line. Starts with the answer so a skimmer can stop reading there. */
  headline: string
  /** Tesla's own support wording for the model, straight from the facts file. */
  support: string
  /** How to confirm in the car, before preparing a drive. */
  confirm: string
  guideHref: string
}

const MODEL_ORDER: readonly TeslaModelKey[] = ['model-s', 'model-3', 'model-x', 'model-y', 'cybertruck']

interface TeslaModelRow {
  model_name: string
  year_start: number
}

/** Build the picker's model list from `tesla_models` rows, falling back to the static mirror. */
export function carModelsFromRows(rows: readonly TeslaModelRow[] = []): CarModel[] {
  const firstYears = new Map<TeslaModelKey, number>()
  for (const row of rows) {
    const key = modelSlugFromCompatibleModelString(row.model_name)
    if (key && Number.isFinite(row.year_start)) firstYears.set(key, row.year_start)
  }
  return MODEL_ORDER.map(key => ({
    key,
    name: TESLA_FACTS.support.models[key].label,
    firstYear: Math.max(firstYears.get(key) ?? 0, TESLA_MODELS[key].firstYear),
  }))
}

/** Model years to offer for one model, newest first. */
export function yearsForModel(model: CarModel, latestYear: number): number[] {
  const years: number[] = []
  for (let y = latestYear; y >= model.firstYear; y--) years.push(y)
  return years
}

export function checkMyCar(model: CarModel, year: number): CarVerdict {
  const since = TESLA_FACTS.support.speakerSince[model.key]
  const support = `${model.name}: ${TESLA_FACTS.support.models[model.key].supported}.`
  const confirm = `${TESLA_FACTS.compatibility.inCarCheck} ${TESLA_FACTS.compatibility.inCarCheckHelp}`
  const guideHref = `/guide/${model.key}`
  const car = `${year} ${model.name}`

  if (since && year === since.year && since.partialYear) {
    return {
      status: 'check',
      headline: `Check first. ${year} is the year the ${model.name} gained the external speaker, so it depends on the build date.`,
      support,
      confirm,
      guideHref,
    }
  }
  if (!since || year >= since.year) {
    return {
      status: 'yes',
      headline: `Yes. A ${car} can play a custom lock sound. It needs ${TESLA_FACTS.firmware.minimumLabel}.`,
      support,
      confirm,
      guideHref,
    }
  }
  return {
    status: 'unlikely',
    headline: `Probably not. A ${car} predates the external speaker.`,
    support,
    confirm,
    guideHref,
  }
}
